import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import {
  CButton,
  CCol,
  CModal,
  CModalBody,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react-pro";
import Swal from "sweetalert";
import API from "src/utils/API";
import Webcam from "react-webcam";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { spkCategoryList } from "src/utils/constant";
import { useDispatch, useSelector } from "react-redux";
import BtnClose from "src/views/kalasanPages/modal/BtnClose";
import ModalApproval from "src/views/kalasanPages/modal/ModalApproval";
import ResponseError from "src/views/kalasanPages/modal/responseError";

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const token = useSelector((state) => state.user.profile.token);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [onPictureTaken, setOnPictureTaken] = useState(null);

  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");

  const [showProductPreparation, setShowProductPreparation] = useState(false);
  const [dataProductPreparation, setDataProductPreparation] = useState([]);
  const [showSop, setShowSop] = useState(false);
  const [sopId, setSopId] = useState(null);
  const camRef = useRef(null);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);

  const stopCameraStream = useCallback(() => {
    if (camRef.current?.stream) {
      camRef.current.stream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  const videoConstraints = {
    width: 500,
    height: 400,
    ...(selectedCamera ? { deviceId: { exact: selectedCamera } } : {}),
  };

  const handleChangeCamera = (deviceId) => {
    if (!deviceId || deviceId === selectedCamera) return;

    setIsSwitchingCamera(true);
    stopCameraStream();
    setSelectedCamera(deviceId);

    setTimeout(() => {
      setIsSwitchingCamera(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, [stopCameraStream]);

  const handleShowImage = (image) => {
    setCurrentImage(image);
    setShowFullscreenImage(true);
  };

  const closeImage = () => {
    setShowFullscreenImage(false);
    setCurrentImage(null);
  };

  const loadCameraDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput",
      );

      setCameraDevices(videoDevices);

      if (videoDevices.length > 0) {
        setSelectedCamera((prev) => prev || videoDevices[0].deviceId);
      }
    } catch (error) {
      console.error("Gagal mengambil daftar kamera:", error);
    }
  };

  const handleShowCamera = async (callback) => {
    setOnPictureTaken(() => callback);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });

      stream.getTracks().forEach((track) => track.stop());

      await loadCameraDevices();
      setShowCamera(true);
    } catch (error) {
      console.error("Gagal mengakses kamera:", error);
      Swal("Gagal mengakses kamera.", "", "error");
    }
  };

  const closeCamera = () => {
    stopCameraStream();
    setShowCamera(false);
    setOnPictureTaken(null);
    setCameraDevices([]);
    setSelectedCamera("");
    setIsSwitchingCamera(false);
  };

  const handleTakePicture = () => {
    const capture = camRef.current?.getScreenshot();
    if (capture) {
      const image = capture.replace("data:image/jpeg;base64,", "");

      // Call the callback function if provided
      if (onPictureTaken) {
        onPictureTaken(image);
      }

      closeCamera();
    } else {
      Swal("Gagal mengambil gambar. Kamera tidak ditemukan.", "", "error");
    }
  };

  const handleShowProductPreparation = (data) => {
    setDataProductPreparation(data);
    setShowProductPreparation(true);
  };

  const handleHideProductPreparation = () => {
    setShowProductPreparation(false);
    setDataProductPreparation([]);
  };

  const { data: dataSopDetail, isLoading: isLoadingSopDetail } = useQuery(
    ["sop-detail", sopId],
    () =>
      API.getSOPById({
        token,
        id: sopId,
      }),
    {
      enabled: !!sopId,
      onSuccess: (res) => {
        // console.log("dataSopDetail: ", res.data);
      },
      onError: (err) => {
        ResponseError(err, dispatch, history);
      },
    },
  );

  const handleShowSop = (id) => {
    setSopId(id);
    setShowSop(true);
  };

  const handleHideSop = () => {
    setShowSop(false);
    setSopId(null);
  };

  return (
    <ModalContext.Provider
      value={{
        handleShowImage,
        handleShowCamera,
        handleTakePicture,
        handleShowProductPreparation,
        handleShowSop,
      }}
    >
      {/* Fullscreen Image Modal */}
      <CModal
        visible={showFullscreenImage}
        onClose={closeImage}
        size="xl"
        alignment="center"
      >
        <CModalBody className="p-0">
          {currentImage && (
            <img
              src={`data:image/jpeg;base64,${currentImage}`}
              alt="Fullscreen view"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          )}
        </CModalBody>
      </CModal>

      {/* Camera Modal */}
      <CModal
        visible={showCamera}
        onClose={closeCamera}
        size="md"
        alignment="center"
      >
        <div
          className="d-flex justify-content-between"
          style={{
            backgroundColor: "#379CC0",
            padding: "15px",
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
          }}
        >
          <div
            className="d-flex align-items-center"
            style={{
              fontWeight: "700",
              fontSize: "16px",
              lineHeight: "26px",
              color: "#fff",
              marginLeft: "0px",
              textTransform: "uppercase",
            }}
          >
            Ambil Foto
          </div>
          <div></div>
          <div
            onClick={closeCamera}
            style={{ marginRight: "0px", cursor: "pointer" }}
          >
            <BtnClose />
          </div>
        </div>
        <CModalBody className="px-0">
          <div className="px-3 mb-3">
            <label className="mb-1 fw-bold">Pilih Kamera</label>
            <select
              className="form-select"
              value={selectedCamera}
              onChange={(e) => handleChangeCamera(e.target.value)}
              disabled={isSwitchingCamera}
            >
              {cameraDevices.length > 0 ? (
                cameraDevices.map((device, index) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Kamera ${index + 1}`}
                  </option>
                ))
              ) : (
                <option value="">Tidak ada kamera terdeteksi</option>
              )}
            </select>
          </div>
          <Webcam
            key={selectedCamera || "default-camera"}
            audio={false}
            ref={camRef}
            screenshotFormat="image/jpeg"
            height={500}
            width={500}
            videoConstraints={videoConstraints}
          />
          <div className="d-flex justify-content-center gap-2">
            <CButton color="danger" variant="outline" onClick={closeCamera}>
              Batalkan
            </CButton>
            <CButton
              color="info"
              className="text-white"
              onClick={handleTakePicture}
            >
              Ambil Foto
            </CButton>
          </div>
        </CModalBody>
      </CModal>

      {/* Product Preparation Modal */}
      <CModal alignment="center" size="lg" visible={showProductPreparation}>
        <div
          className="d-flex justify-content-between"
          style={{
            backgroundColor: "#379CC0",
            padding: "15px",
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
          }}
        >
          <div
            className="d-flex align-items-center"
            style={{
              fontWeight: "700",
              fontSize: "16px",
              lineHeight: "26px",
              color: "#fff",
              marginLeft: "0px",
            }}
          >
            Daftar Penyediaan Bahan
          </div>
          <div
            onClick={handleHideProductPreparation}
            style={{ marginRight: "0px", cursor: "pointer" }}
          >
            <BtnClose />
          </div>
        </div>
        <CModalBody
          style={{
            margin: "0px 10px 0px 10px",
            overflowY: "auto",
            maxHeight: "85vh",
          }}
        >
          <div className="d-flex flex-column gap-3">
            <CTable className="table-top-bottom-content" responsive>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell scope="col">No</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Produk Bahan</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Kategori</CTableHeaderCell>
                  <CTableHeaderCell scope="col">
                    Stok Dibutuhkan
                  </CTableHeaderCell>
                  <CTableHeaderCell scope="col">Stok Diminta</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Stok Tersedia</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody className="tableData">
                {dataProductPreparation?.length > 0 ? (
                  dataProductPreparation?.map((item, index) => (
                    <CTableRow key={index}>
                      <CTableDataCell scope="row">{index + 1}</CTableDataCell>
                      <CTableDataCell>{item.product_name}</CTableDataCell>
                      <CTableDataCell>{item.category_name}</CTableDataCell>
                      <CTableDataCell>
                        {item.qty} {item.unit_name}
                      </CTableDataCell>
                      <CTableDataCell>
                        {spkCategoryList.includes(item.category_name)
                          ? item.total_request_spk
                          : item.total_request_on_progress}{" "}
                        {item.unit_name}
                      </CTableDataCell>
                      <CTableDataCell>
                        {item.qty_stock} {item.unit_name}
                      </CTableDataCell>
                    </CTableRow>
                  ))
                ) : (
                  <CTableRow>
                    <CTableDataCell className="font-italic" colSpan="10">
                      Tidak ada data
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>
        </CModalBody>
      </CModal>

      {/* tambahan buat modal approval */}
      <ModalApproval />

      {/* SOP Modal */}
      <CModal alignment="center" size="lg" visible={showSop} scrollable>
        <div
          className="d-flex justify-content-between"
          style={{
            backgroundColor: "#379CC0",
            padding: "15px",
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
          }}
        >
          <div
            className="d-flex align-items-center"
            style={{
              fontWeight: "700",
              fontSize: "16px",
              lineHeight: "26px",
              color: "#fff",
              marginLeft: "0px",
            }}
          >
            SOP
          </div>
          <div
            onClick={handleHideSop}
            style={{ marginRight: "0px", cursor: "pointer" }}
          >
            <BtnClose />
          </div>
        </div>
        <CModalBody
          style={{
            margin: "0px 10px 0px 10px",
            overflowY: "auto",
            maxHeight: "85vh",
          }}
        >
          {isLoadingSopDetail ? (
            <div>Loading...</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              <CRow className="mb-2">
                <CCol>
                  <div className="fw-bold fs-5 mb-2">
                    {dataSopDetail?.data?.title || "-"}
                  </div>
                  <div className="fs-6">
                    {dataSopDetail?.data?.description || "-"}
                  </div>
                </CCol>
              </CRow>

              <CTable className="table-top-bottom-content" responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell scope="col">No</CTableHeaderCell>
                    <CTableHeaderCell scope="col">Intruksi</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody className="tableData">
                  {dataSopDetail?.data?.steps?.length > 0 ? (
                    dataSopDetail?.data?.steps?.map((item, index) => (
                      <CTableRow key={index}>
                        <CTableDataCell scope="row">
                          {item.order}
                        </CTableDataCell>
                        <CTableDataCell>{item.description}</CTableDataCell>
                      </CTableRow>
                    ))
                  ) : (
                    <CTableRow>
                      <CTableDataCell className="font-italic" colSpan="10">
                        Tidak ada data
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            </div>
          )}
        </CModalBody>
      </CModal>

      {children}
    </ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useModalContext = () => {
  return useContext(ModalContext);
};
